
import { type FC, type ReactNode, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Icon } from '@/components/ui/icon'
import { springSmooth } from '@/lib/animations'

interface CollapsibleSectionProps {
  /** Section title */
  title: string
  /** Whether the section starts open */
  defaultOpen?: boolean
  /** Section content */
  children: ReactNode
  /**
   * Layout variant:
   * - "card": bg-card wrapper with px-1 padding (shader/controlnet style)
   * - "plain": minimal border-b wrapper (image node style)
   */
  variant?: 'card' | 'plain'
}

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
  title,
  defaultOpen = true,
  children,
  variant = 'card',
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  if (variant === 'plain') {
    return (
      <div className="border-b border-border">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="group flex w-full items-center justify-between px-3 py-2 transition-colors"
        >
          <span className="text-sm font-semibold">{title}</span>
          <Icon
            name={isOpen ? 'chevron-up' : 'chevron-down'}
            className="h-4 w-4 text-muted-foreground"
          />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="plain-content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={springSmooth}
              style={{ overflow: "hidden" }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  // variant === 'card'
  return (
    <div className="border-b border-border bg-card pb-2 pt-0 px-1">
      <div className="flex flex-col gap-3 pt-2">
        <div className="flex items-center h-7 px-2">
          <span className="text-sm font-semibold flex-1">{title}</span>
          <button onClick={() => setIsOpen(!isOpen)}>
            <Icon
              name={isOpen ? 'chevron-up' : 'chevron-down'}
              className="h-4 w-4 text-muted-foreground"
            />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="card-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springSmooth}
            style={{ overflow: "hidden" }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
